import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import { di } from '../../state/inversify.config';
import { SymptomAssessmentIntro } from '../../components/symptomAssessment/symptomAssessmentIntro';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { AccountTypes } from '../../components/changeUser/accountTypes.enum';
import { SymptomAssessmentDomainService } from './symptomAssessmentDomainService';
import { SymptomAsessmentStepper } from '../../components/symptomAssessment/symptomAsessmentStepper';
import symptomAssessmentSpecs from './symptom_assessment_v1.0.json';
import { SymptomAssessmentSummary } from '../../components/symptomAssessment/symptomAssessmentSummary';
import { AssessmentRepository } from '../../state/reduxRepositories/assessments/assessmentRepository';
import { version } from '../../version';

export function CreateSymptomAssessment() {
  const {
    state: { selectedPlayerId },
  } = useLocation();
  const navigate = useNavigate();

  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState<number[]>([]);

  const module = symptomAssessmentSpecs;
  const steps = module.assessmentModuleSpecs.questions;
  const totalSteps = steps.length;

  const symptomAssessmentDomainService = di.get<SymptomAssessmentDomainService>(
    SymptomAssessmentDomainService,
  );

  const assessmentRepository =
    di.get<AssessmentRepository>(AssessmentRepository);

  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();
  const isGuardianUser = loggedInUser?.accountType === AccountTypes.Guardian;
  const userFirstName = `${loggedInUser?.firstName}`;

  const player =
    symptomAssessmentDomainService.selectPlayerById(selectedPlayerId);
  const playerFirstName = `${player?.firstName}`;

  const guardianEmails =
    symptomAssessmentDomainService.selectGuardiansForOrgPlayer(
      selectedPlayerId,
    );

  const handleAnswer = (
    event: React.MouseEvent<HTMLElement>,
    newAnswer: string,
    answerStep: number,
  ) => {
    answers[answerStep] = parseInt(newAnswer, 10);
    setAnswers([...answers]);
  };

  const score = answers.filter((ans) => ans !== 0).length;

  const handleSubmit = () => {
    if (!player) {
      return;
    }

    const data = steps.map((step, index) => {
      return {
        ...step,
        answer: answers[index],
      };
    });

    assessmentRepository.dispatchCreate({
      assessment: {
        name: 'symptom assessment',
        assessmentType: symptomAssessmentSpecs.info.identifier,
        assessmentVersion: symptomAssessmentSpecs.info.version,
        createdOnSoftwareVersion: version,
        info: JSON.stringify(symptomAssessmentSpecs),
        assessment: { data, score, totalSteps },
      },
      playerId: player.id,
      playerFullName: `${player.firstName} ${player.lastName}`,
      timeZoneOffset: new Date().getTimezoneOffset(),
    });

    setCompleted(!completed);
  };

  const navigateToPlayer = () => {
    if (player) {
      navigate(
        `/${AccountTypes.Guardian}/player-profile?selectedPlayerId=${player.id}`,
      );
    }
  };

  return (
    <Container>
      {!started && !completed && (
        <SymptomAssessmentIntro
          setStarted={() => setStarted(!started)}
          isGuardianUser={isGuardianUser}
          playerFirstName={playerFirstName}
          guardianEmails={guardianEmails}
        />
      )}

      {started && !completed && (
        <SymptomAsessmentStepper
          setStarted={() => setStarted(!started)}
          steps={steps}
          answers={answers}
          handleAnswer={handleAnswer}
          handleSubmit={handleSubmit}
          playerFirstName={playerFirstName}
        />
      )}

      {started && completed && (
        <SymptomAssessmentSummary
          playerFirstName={playerFirstName}
          userFirstName={userFirstName}
          isGuardianUser={isGuardianUser}
          guardianEmails={guardianEmails}
          score={score}
          totalSteps={totalSteps}
          summaryCloseAction={navigateToPlayer}
        />
      )}
    </Container>
  );
}
